import { forwardRef, LegacyRef, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import clsx from 'clsx';
import { InputLabel } from '@mui/material';

const Root = styled('div')(({ theme }) => ({
  borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : 'rgba(0, 0, 0, 0.23)',
  '& .rdw-dropdown-selectedtext': {
    color: 'inherit',
  },
  '& .rdw-editor-toolbar': {
    borderWidth: '0 0 1px 0!important',
    margin: '0!important',
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : undefined,
    borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : undefined,
  },
  '& .rdw-editor-main': {
    padding: '8px 12px',
    height: `${256}px!important`,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : undefined,
    color: theme.palette.mode === 'dark' ? theme.palette.text.primary : undefined,
  },
  // Dark mode styles for toolbar options
  ...(theme.palette.mode === 'dark' && {
    '& .rdw-option-wrapper': {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      minWidth: '28px',
      height: '28px',
      '&:hover': {
        boxShadow: `1px 1px 0px ${theme.palette.divider}`,
        backgroundColor: theme.palette.action.hover,
      },
      // Invert icons to make them visible on dark background
      '& img': {
        filter: 'invert(1)',
      },
    },
    '& .rdw-option-active': {
      backgroundColor: theme.palette.action.selected,
      boxShadow: `1px 1px 0px ${theme.palette.divider} inset`,
    },
    '& .rdw-option-disabled': {
      opacity: 0.4,
    },
    '& .rdw-dropdown-wrapper': {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      '&:hover': {
        boxShadow: `1px 1px 0px ${theme.palette.divider}`,
        backgroundColor: theme.palette.action.hover,
      },
      // Invert dropdown carets
      '& .rdw-dropdown-carettoopen, & .rdw-dropdown-carettoclose': {
        borderTopColor: theme.palette.text.primary,
      },
    },
    '& .rdw-dropdown-optionwrapper': {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
    },
    '& .rdw-dropdownoption-default': {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    '& .rdw-dropdownoption-highlighted': {
      backgroundColor: theme.palette.action.selected,
    },
    '& .rdw-colorpicker-wrapper': {
      '& img': {
        filter: 'invert(1)',
      },
    },
    '& .rdw-emoji-wrapper': {
      '& img': {
        filter: 'invert(1)',
      },
    },
    '& .public-DraftEditorPlaceholder-root': {
      color: theme.palette.text.secondary,
    },
    '& .public-DraftEditor-content': {
      color: theme.palette.text.primary,
    },
  }),
}));

interface Props {
  value?: string;
  className: string;
  onChange: any;
  defaultValue: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
}

// function removeInlineStyles(html: string): string {
//   return html.replace(/style\s*=\s*"(?:\\.|[^"\\])*"/g, '');
// }

const WYSIWYGEditor = forwardRef((props: Props, ref: LegacyRef<HTMLDivElement>) => {
  const [editorState, setEditorState] = useState(() => {
    const initialContent = props.value || props.defaultValue;
    if (initialContent) {
      const blocksFromHtml = htmlToDraft(initialContent);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      return EditorState.createWithContent(contentState);
    } else {
      return EditorState.createEmpty();
    }
  });

  useEffect(() => {
    if (props.value) {
      const currentContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      if (props.value !== currentContent) {
        const blocksFromHtml = htmlToDraft(props.value);
        const { contentBlocks, entityMap } = blocksFromHtml;
        const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
        const newEditorState = EditorState.createWithContent(contentState);
        setEditorState(newEditorState);
      }
    }
  }, [props.value]);

  function onEditorStateChange(_editorState: EditorState) {
    setEditorState(_editorState);
    return props.onChange(draftToHtml(convertToRaw(_editorState.getCurrentContent())));
  }

  return (
    <Root
      className={clsx('rounded-4 border-1 border-solid overflow-hidden w-full', props.className)}
      ref={ref}
    >
      {props.label ? (
        <InputLabel htmlFor='editor' className='ml-10 mt-10 font-bold'>
          {props.label}
        </InputLabel>
      ) : null}
      <Editor
        placeholder={props.placeholder ?? 'Enter text here...'}
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        wrapperClassName='editor-wrapper'
        editorClassName='editor'
        readOnly={props.disabled ?? false}
      />
    </Root>
  );
});

WYSIWYGEditor.displayName = 'WysiwigEditor';
export default WYSIWYGEditor;
